import React, { useState, useEffect, useRef } from "react";
import { useDraftAutosave } from "../lib/useDraftAutosave";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError, formatSAR } from "../lib/api";
import { Sparkles, ArrowRight, Loader2, Upload as UploadIcon, X, Phone, MessageSquare, ShieldCheck, Building2 } from "lucide-react";
import { FEATURES } from "../lib/features";
import { SpecChecklist } from "../components/sell/SpecChecklist";
import { SAUDI_CITIES as CITIES } from "../data/locations";
import { validateDescription, DESCRIPTION_MAX_LEN } from "../lib/listingDescription";
import SaleOptionsChips from "../components/SaleOptionsChips";

const VEHICLE_TYPES = [
  ["used", "Used", "مستعملة"],
  ["new", "New (dealer)", "جديدة (تاجر)"],
  // iter276 — additive-only listing state. Existing listings are NEVER
  // touched or migrated; this value simply becomes selectable for any
  // future edit or new post that qualifies. Backend accepts free-form
  // `type` strings so no server-side change is needed.
  ["new_customs_card", "New — Customs Card", "🆕 جديدة – بطاقة جمركية"],
  ["rental", "Rental", "تأجير"],
  ["salvage", "Salvage", "سكراب"],
  ["motorcycle", "Motorcycle", "دراجة"],
  ["heavy", "Heavy Equipment", "المعدات الثقيلة"],
  ["boat", "Boat / Yacht", "يخت / زورق"],
  ["export", "Export-ready", "للتصدير"],
];

// Comprehensive list of body styles a Saudi seller might list — covers
// passenger cars, off-road, utility, and luxury.
const BODY_TYPES = [
  ["Sedan", "Sedan", "سيدان"],
  ["SUV", "SUV", "دفع رباعي (SUV)"],
  ["Crossover", "Crossover", "كروس أوفر"],
  ["Off-road", "Off-road", "دفع رباعي (طرق وعرة)"],
  ["Hatchback", "Hatchback", "هاتشباك"],
  ["Coupe", "Coupe", "كوبيه"],
  ["Convertible", "Convertible", "مكشوفة"],
  ["Cabriolet", "Cabriolet", "كابريوليه"],
  ["Roadster", "Roadster", "رودستر"],
  ["Muscle", "Muscle", "أمريكي عضلي (Muscle)"],
  ["Sport", "Sport", "رياضية"],
  ["Wagon", "Wagon", "ستيشن (Wagon)"],
  ["Pickup", "Pickup", "بيك أب"],
  ["Van", "Van", "فان / باص صغير"],
  ["Minivan", "Minivan", "مينيفان عائلي"],
  ["Micro", "Micro", "اقتصادية صغيرة (Micro)"],
  ["Limousine", "Limousine", "ليموزين"],
  ["Truck", "Truck", "شاحنة"],
  ["Bus", "Bus", "حافلة / باص"],
  ["Other", "Other", "أخرى"],
];

// iter188 — city dropdowns now consume the unified registry so every
// selector shows the full Saudi city + governorate list. Do NOT add a
// local list here; edit /app/frontend/src/data/locations.js instead.

const TRANSMISSIONS = [
  ["Automatic", "Automatic", "أوتوماتيك"],
  ["Manual", "Manual", "عادي"],
  ["DCT", "DCT (Dual-clutch)", "DCT ثنائي القابض"],
  ["CVT", "CVT", "CVT متغير"],
];

// Motorcycle-specific body / style categories (Sport, Cruiser, Touring…).
const MOTORCYCLE_BODY_TYPES = [
  ["Sport",       "Sport",              "رياضية (Sport)"],
  ["Naked",       "Naked / Streetfighter","نيكد / ستريتفايتر"],
  ["Cruiser",     "Cruiser",            "كروزر"],
  ["Touring",     "Touring",            "تورينج (سياحية)"],
  ["Adventure",   "Adventure",          "أدفنتشر (طرق وعرة)"],
  ["Enduro",      "Enduro / Dirt bike", "إندورو / ترابية"],
  ["Chopper",     "Chopper",            "شوبر"],
  ["Scooter",     "Scooter",            "سكوتر"],
  ["ATV",         "ATV / Quad",         "ATV رباعية"],
  ["Electric",    "Electric",           "كهربائية"],
  ["Other",       "Other",              "أخرى"],
];

// Heavy Equipment — comprehensive taxonomy (iter326).
// Bilingual labels; the internal key is stable so existing ads (Tractor,
// Tipper, FlatBed…) are preserved. New entries are appended below.
const HEAVY_BODY_TYPES = [
  // ── Trucks & haulage ──────────────────────────────────────────────
  ["Tractor",     "Tractor head",       "رأس تريلة"],
  ["Trailer",     "Trailer",            "تريلا / مقطورة"],
  ["Tipper",      "Tipper / Dump truck","قلاب"],
  ["TipperTrailer","Tipper trailer",    "قلاب تريلا"],
  ["FlatBed",     "Flatbed",            "مسطّح"],
  ["Lowbed",      "Lowbed / Equipment transporter","نقل معدات ثقيلة"],
  ["BoxTruck",    "Box truck",          "لوري صندوق مغلق"],
  ["Lorry",       "Lorry",              "لوري"],
  ["Refrigerated","Refrigerated truck", "براد / ثلاجة"],
  ["Pickup",      "Heavy pickup",       "بيك أب ثقيل"],
  ["SuctionTruck","Suction truck",      "شاحنة شفط"],
  // ── Tankers ───────────────────────────────────────────────────────
  ["Tanker",              "Tanker (water)",       "صهريج ماء"],
  ["TankerFuel",          "Tanker (fuel)",        "صهريج محروقات"],
  ["TankerSewage",        "Tanker (sewage)",      "صهريج صرف صحي"],
  ["TankerWaterSpray",    "Tanker (water spray)", "صهريج رش مياه"],
  // ── Buses / Vans ──────────────────────────────────────────────────
  ["Bus",         "Bus / Coach",        "حافلة / باص"],
  ["Van",         "Van",                "فان"],
  // ── Excavation / earthmoving ─────────────────────────────────────
  ["Excavator",         "Excavator (Poclain)",     "بوكلين / حفار"],
  ["MiniExcavator",     "Mini excavator",          "حفارة صغيرة"],
  ["CrawlerExcavator",  "Crawler excavator",       "حفارة جنزير"],
  ["WheelExcavator",    "Wheeled excavator",       "حفارة بعجلات"],
  ["LongReachExcavator","Long-reach excavator",    "حفارة طويلة الذراع"],
  ["Loader",            "Wheel loader (Shovel)",   "شيول / لودر بعجلات"],
  ["MiniLoader",        "Compact loader",          "لودر صغير"],
  ["Bulldozer",         "Bulldozer",               "بلدوزر"],
  ["Grader",            "Motor grader",            "قريدر"],
  ["Bobcat",            "Skid-steer (Bobcat)",     "بوب كات"],
  ["Backhoe",           "Backhoe loader (JCB)",    "جي سي بي"],
  ["Compactor",         "Plate compactor",         "دكاكة"],
  ["Roadroller",        "Road roller",             "مدحلة طرق"],
  ["Tractor2",          "Agricultural tractor",    "تراكتور زراعي"],
  // ── Cranes & lifting ──────────────────────────────────────────────
  ["Crane",             "Crane truck",             "شاحنة رافعة"],
  ["MobileCrane",       "Mobile crane",            "رافعة متحركة"],
  ["TowerCrane",        "Tower crane",             "رافعة برجية"],
  ["TelescopicCrane",   "Telescopic crane",        "رافعة تلسكوبية"],
  ["Telehandler",       "Telehandler",             "مناولة تلسكوبية"],
  ["Forklift",          "Forklift",                "رافعة شوكية"],
  ["ScissorLift",       "Scissor lift",            "مقص رفع"],
  ["BoomLift",          "Boom lift / Man lift",    "منصة رفع / رافعة أفراد"],
  ["TowWinch",          "Tow winch",               "ونش سحب"],
  ["Winch",             "Winch",                   "وينش"],
  // ── Concrete & asphalt ────────────────────────────────────────────
  ["MixerTruck",        "Concrete mixer truck",    "خلاطة أسمنت"],
  ["ConcretePump",      "Concrete pump",           "مضخة خرسانة"],
  ["ConcreteVibrator",  "Concrete vibrator",       "هزاز خرسانة"],
  ["AsphaltMachine",    "Asphalt machine",         "ماكينة سفلتة"],
  ["AsphaltPaver",      "Asphalt paver",           "فرادة أسفلت"],
  ["AsphaltCutter",     "Asphalt cutter",          "ماكينة قص أسفلت"],
  ["RoadMarker",        "Road marking machine",    "ماكينة تخطيط طرق"],
  // ── Power & compressors ───────────────────────────────────────────
  ["Generator",         "Generator",               "مولد كهرباء"],
  ["Compressor",        "Industrial air compressor","كمبروسر هواء صناعي"],
  ["DrillCompressor",   "Drilling air compressor", "كمبروسر هواء للحفريات"],
  ["Welder",            "Industrial welder",       "ماكينة لحام صناعية"],
  // ── Storage / containers ──────────────────────────────────────────
  ["Container",         "Shipping container",      "حاوية / كونتينر"],
  ["WaterTank",         "Water tank",              "خزان مياه"],
  ["FuelTank",          "Fuel tank",               "خزان وقود"],
  // ── Mining, drilling, crushing ───────────────────────────────────
  ["WellDrilling",      "Well drilling equipment", "معدات حفر آبار"],
  ["Crusher",           "Crusher",                 "كسارة"],
  ["Screen",            "Screening plant",         "غربال"],
  ["ConveyorBelt",      "Conveyor belt",           "سير ناقل"],
  ["QuarryEquipment",   "Quarry equipment",        "معدات محاجر"],
  ["MiningEquipment",   "Mining equipment",        "معدات تعدين"],
  ["DemolitionEquipment","Demolition / breakers",  "معدات تكسير"],
  // ── Other ─────────────────────────────────────────────────────────
  ["Other",       "Other heavy equipment", "معدة ثقيلة أخرى"],
];

const FUELS = [
  ["Petrol", "Petrol", "بنزين"],
  ["Diesel", "Diesel", "ديزل"],
  ["Hybrid", "Hybrid", "هايبرد"],
  ["Electric", "Electric", "كهربائي"],
  ["LPG", "LPG", "غاز مسال"],
];

// ─────────────────────────────────────────────────────────────────────
// Boat / Yacht international-standard specification enums.
// Shown only when Vehicle Type = "boat". Keeps car flow untouched.
// ─────────────────────────────────────────────────────────────────────
const BOAT_TYPES = [
  ["motor_yacht",     "Motor Yacht",       "يخت محرك"],
  ["sailing_yacht",   "Sailing Yacht",     "يخت شراعي"],
  ["superyacht",      "Superyacht (24m+)", "سوبريخت (+24م)"],
  ["mega_yacht",      "Mega Yacht (60m+)", "ميغا يخت (+60م)"],
  ["sport_fisherman", "Sport Fisherman",   "قارب صيد رياضي"],
  ["cabin_cruiser",   "Cabin Cruiser",     "كابين كروزر"],
  ["center_console",  "Center Console",    "سنتر كونسول"],
  ["bowrider",        "Bowrider",          "باوريدر"],
  ["deck_boat",       "Deck Boat",         "قارب سطح مفتوح"],
  ["pontoon",         "Pontoon",           "بونتون"],
  ["catamaran",       "Catamaran",         "كتاماران (بدنَين)"],
  ["trimaran",        "Trimaran",          "تريماران (ثلاثة أبدان)"],
  ["speed_boat",      "Speed Boat",        "قارب سريع"],
  ["rib",             "RIB (Rigid Inflatable)", "قارب مطاطي صلب (RIB)"],
  ["jet_ski",         "Personal Watercraft / Jet-Ski", "جيت سكي"],
  ["fishing_boat",    "Fishing Boat",      "قارب صيد"],
  ["house_boat",      "House Boat",        "قارب سكني"],
  ["dinghy",          "Dinghy",            "قارب صغير (دنجي)"],
  ["other",           "Other",             "أخرى"],
];

const BOAT_HULLS = [
  ["fiberglass", "Fiberglass (GRP)", "فايبرغلاس"],
  ["aluminum",   "Aluminum",         "ألمنيوم"],
  ["steel",      "Steel",            "فولاذ"],
  ["wood",       "Wood",             "خشب"],
  ["composite",  "Composite",        "مركب"],
  ["inflatable", "Inflatable",       "قابل للنفخ"],
];

const BOAT_ENGINES = [
  ["outboard",   "Outboard",           "محرك خارجي"],
  ["inboard",    "Inboard",            "محرك داخلي"],
  ["sterndrive", "Stern-drive (I/O)",  "ستيرن درايف (I/O)"],
  ["jet",        "Jet Drive",          "دفع نفاث"],
  ["sail",       "Sail (no engine)",   "شراعي (بلا محرك)"],
  ["electric",   "Electric",           "كهربائي"],
];

const BOAT_FUELS = [
  ["gasoline", "Gasoline",       "بنزين"],
  ["diesel",   "Diesel",         "ديزل"],
  ["electric", "Electric",       "كهربائي"],
  ["hybrid",   "Hybrid",         "هايبرد"],
  ["sail",     "Sail (no fuel)", "شراعي — بدون وقود"],
];

const BOAT_CONDITIONS = [
  ["new",       "New (Dealer)",  "جديد (تاجر)"],
  ["used",      "Used",          "مستعمل"],
  ["rental",    "Rental",        "للتأجير"],
  ["charter",   "Charter",       "شارتر"],
  ["salvage",   "Salvage",       "تصادم / سكراب"],
];

const MAX_IMAGES = 8;

// iter317 — client-side image normalisation, added to fix upload hangs on
// mobile (esp. iPhone) that caused the reported "spinner-forever +
// Something went wrong" bug on marine listings.
//
// Root cause of the hang: iPhone photos are usually 3-8 MB HEIC or ~5 MB
// JPEG at 4032×3024. On 4G upload speeds these routinely exceed the
// axios 30 s timeout, and HEIC is silently unusable by other browsers
// even when the raw bytes make it to storage. We resize + re-encode to
// JPEG in the browser BEFORE hitting `/api/upload`, so the network
// payload becomes ~250-500 KB and the format is universally viewable.
//
// The helper is best-effort: if the browser cannot decode the file
// (e.g. HEIC on non-Safari), we fall back to the original bytes so the
// backend gets a chance to serve them as-is.
const IMAGE_MAX_EDGE = 1920;
const IMAGE_JPEG_QUALITY = 0.85;
async function normalizeImageForUpload(file) {
  if (!file || typeof file !== "object") return file;
  if (!(file.type || "").startsWith("image/")) return file;
  // Anything already small and non-HEIC — skip re-encode.
  const isHeic = /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || "");
  if (!isHeic && file.size < 900 * 1024) return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objectUrl;
    });
    const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", IMAGE_JPEG_QUALITY),
    );
    if (!blob) return file;
    const outName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], outName, { type: "image/jpeg", lastModified: Date.now() });
  } catch (_e) {
    // Decoding failed (e.g. HEIC on Chrome). Let the original bytes
    // go through so we don't silently drop the user's photo.
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function SellPage() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 2F — businesses the current user can post as
  const [myBusinesses, setMyBusinesses] = useState([]);
  useEffect(() => {
    if (!user) return;
    apiClient.get("/platform/businesses/mine")
      .then(({ data }) => setMyBusinesses(data.items || []))
      .catch(() => setMyBusinesses([]));
  }, [user]);
  // Pre-select the business from ?business_id= (invoked from DashListings CTA)
  useEffect(() => {
    const bid = searchParams.get("business_id");
    if (bid && myBusinesses.some((b) => b.id === bid)) {
      setForm((f) => ({ ...f, business_id: bid }));
    }
  }, [searchParams, myBusinesses]);
  const [form, setForm] = useState({
    type: "used", make: "", model: "", year: 2023, price_sar: 100000,
    mileage_km: 0, transmission: "Automatic", fuel: "Petrol", color: "",
    city: "Riyadh", category: "cars", body: "Sedan",
    description: "", seller_type: "individual", seller_name: "",
    images: [],
    contact_phone: "", contact_whatsapp: "", prefer_contact: "phone",
    periodic_inspection_valid: false, periodic_inspection_expiry: "",
    escrow_enabled: false,
    // Phase 2A — marketplace-wide "استقبال العروض" toggle
    price_visibility: "shown", // shown | hidden
    // iter247 — optional sale-method chips (0..4). Values whitelisted server-side.
    sale_options: [],
    // Phase 2C — optional business attribution (Post-as-Business)
    business_id: "",
    branch_id: "",
    // Auction-only mode: listing_type is fixed to "auction" — the previous
    // fixed-price toggle was removed per product decision.
    listing_type: "fixed_price",
    starting_bid_sar: 50000,
    reserve_price_sar: "",
    buy_now_price_sar: "",
    min_bid_increment_sar: 500,
    auction_duration_days: 7,
    // ── Boat / Yacht spec fields (only used when type === "boat") ──
    boat_type: "motor_yacht",
    boat_condition: "used",
    boat_hull_material: "fiberglass",
    boat_engine_type: "outboard",
    boat_fuel_type: "gasoline",
    boat_length_m: "",       // LOA (Length Overall) in meters — international standard
    boat_beam_m: "",         // beam width in meters
    boat_draft_m: "",        // draft in meters
    boat_engine_power_hp: "",
    boat_engines_count: 1,
    boat_max_speed_knots: "",
    boat_cabins: 0,
    boat_berths: 0,
    boat_heads: 0,           // bathrooms
    boat_capacity: "",       // passenger capacity
    boat_engine_hours: 0,    // equivalent of mileage
    boat_fuel_capacity_l: "",
    boat_water_capacity_l: "",
    boat_flag: "",           // country of registration
    // ── Motorcycle-specific fields (only used when type === "motorcycle") ──
    moto_engine_cc: "",       // engine capacity (cubic centimeters)
    moto_cylinders: "",       // number of cylinders
    moto_cooling: "liquid",   // "air" | "liquid"
    moto_abs: false,          // ABS brakes present
    // ── Heavy-truck-specific fields (only used when type === "heavy") ──
    truck_payload_tons: "",   // payload capacity (metric tons)
    truck_gvw_tons: "",       // Gross Vehicle Weight
    truck_axles: 2,           // number of axles
    truck_cargo_m3: "",       // cargo box volume (cubic meters)
    truck_engine_l: "",       // engine displacement (liters)
    truck_engine_hp: "",      // engine horsepower
    truck_cab_type: "day",    // "day" | "sleeper" | "crew"
    // iter327 (Phase 2) — Dynamic heavy-equipment specs keyed by admin-defined
    // spec `key`. Stored under a new optional `heavy_specs` dict on the vehicle
    // document so legacy ads remain fully backward-compatible.
    heavy_specs: {},
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [valBusy, setValBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  // iter327 — Dynamic heavy-equipment specs (Phase 2). Fetched from the
  // Super-Admin-managed taxonomy every time the seller changes the
  // selected heavy-equipment sub-type (form.body). Additive-only; when the
  // list is empty (no admin-configured fields) nothing new is rendered so
  // the SellPage keeps working exactly like Phase 1.
  const [heavyDynamicSpecs, setHeavyDynamicSpecs] = useState([]);
  useEffect(() => {
    if (form.type !== "heavy" || !form.body) {
      setHeavyDynamicSpecs([]);
      return;
    }
    let alive = true;
    apiClient
      .get(`/heavy-taxonomy/specs/${encodeURIComponent(form.body)}`)
      .then(({ data }) => {
        if (!alive) return;
        setHeavyDynamicSpecs(data.items || []);
      })
      .catch(() => { if (alive) setHeavyDynamicSpecs([]); });
    return () => { alive = false; };
  }, [form.type, form.body]);

  const formRef = useRef(null);
  const draft = useDraftAutosave({ form, setForm, formRef });
  if (user === undefined) return <div className="p-10 text-[#6b6b6b]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/sell" }} replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ── Dynamic type-aware labels ────────────────────────────────────────
  // Different vehicle categories need different wording throughout the form.
  // Instead of hard-coding "المركبة" everywhere, we resolve a per-type noun
  // (definite Arabic form + English) that all labels/placeholders reuse.
  const typeCopy = (() => {
    switch (form.type) {
      case "boat":
        return {
          ar_def: "اليخت / الزورق", en_def: "yacht / boat",
          ar_your: "يختك / زورقك", en_your: "your yacht / boat",
          ar_make_ex: "مثال: Azimut، Sunseeker، Sea Ray", en_make_ex: "e.g. Azimut, Sunseeker, Sea Ray",
          ar_make_hint: "الشركة المصنّعة للقارب (Azimut، Ferretti، Princess، Beneteau…)",
          en_make_hint: "Boat builder (Azimut, Ferretti, Princess, Beneteau…)",
          ar_model_ex: "مثال: Grande 32M", en_model_ex: "e.g. Grande 32M",
          ar_model_hint: "اسم الطراز / السلسلة", en_model_hint: "The model / series name",
          ar_desc_placeholder: "اكتب وصفاً موجزاً يُبرز مميزات اليخت/الزورق: التجهيزات، الصيانة، الحالة العامة، وأي إضافات خاصة.",
          en_desc_placeholder: "Briefly describe the yacht/boat: equipment, maintenance history, general condition, and any special features.",
        };
      case "motorcycle":
        return {
          ar_def: "الدراجة", en_def: "motorcycle",
          ar_your: "دراجتك", en_your: "your motorcycle",
          ar_make_ex: "مثال: هوندا، ياماها، هارلي", en_make_ex: "e.g. Honda, Yamaha, Harley-Davidson",
          ar_make_hint: "الشركة المصنّعة (هوندا، دوكاتي، بي إم دبليو، كاواساكي…)",
          en_make_hint: "Manufacturer (Honda, Ducati, BMW, Kawasaki…)",
          ar_model_ex: "مثال: CBR 600RR", en_model_ex: "e.g. CBR 600RR",
          ar_model_hint: "اسم الطراز أو السلسلة", en_model_hint: "Model / series name",
          ar_desc_placeholder: "اكتب وصفاً موجزاً يبرز مميزات الدراجة: السعة اللترية، عدد الأميال، الصيانة، الإكسسوارات، والحالة العامة.",
          en_desc_placeholder: "Briefly describe the motorcycle: engine cc, mileage, service history, add-ons, and condition.",
        };
      case "heavy":
        return {
          ar_def: "الشاحنة", en_def: "truck",
          ar_your: "شاحنتك", en_your: "your truck",
          ar_make_ex: "مثال: هينو، مان، فولفو، دايو", en_make_ex: "e.g. Hino, MAN, Volvo, Daewoo",
          ar_make_hint: "الشركة المصنّعة للشاحنة (هينو، إسوزو، مرسيدس، مان، فولفو…)",
          en_make_hint: "Truck maker (Hino, Isuzu, Mercedes, MAN, Volvo…)",
          ar_model_ex: "مثال: FMX 500", en_model_ex: "e.g. FMX 500",
          ar_model_hint: "اسم الطراز / التجهيز", en_model_hint: "Model / trim name",
          ar_desc_placeholder: "اكتب وصفاً موجزاً للشاحنة: الحمولة، عدد المحاور، حالة المحرك والقير، تاريخ الصيانة، وأي تجهيزات خاصة (رافعة، براد، صهريج…).",
          en_desc_placeholder: "Briefly describe the truck: payload, axles, engine & gearbox condition, service history, and any special equipment (crane, refrigerated body, tanker…).",
        };
      case "salvage":
        return {
          ar_def: "المركبة", en_def: "vehicle",
          ar_your: "مركبتك", en_your: "your vehicle",
          ar_make_ex: "مثال: تويوتا", en_make_ex: "e.g. Toyota",
          ar_make_hint: "الصانع (تويوتا، بي إم دبليو، فورد…)",
          en_make_hint: "Manufacturer (Toyota, BMW, Ford…)",
          ar_model_ex: "مثال: كامري XLE", en_model_ex: "e.g. Camry XLE",
          ar_model_hint: "اسم الطراز أو الفئة — ليس سنة الصنع",
          en_model_hint: "The trim / model name — not the year",
          ar_desc_placeholder: "وضّح مصدر التلف (تصادم / حريق / غمر ماء)، الأجزاء السليمة، وأي قطع غيار مرفقة.",
          en_desc_placeholder: "Explain damage source (crash / fire / flood), salvageable parts, and any spares included.",
        };
      default:
        return {
          ar_def: "المركبة", en_def: "vehicle",
          ar_your: "مركبتك", en_your: "your vehicle",
          ar_make_ex: "مثال: تويوتا", en_make_ex: "e.g. Toyota",
          ar_make_hint: "الصانع (تويوتا، بي إم دبليو، فورد…)",
          en_make_hint: "Manufacturer (Toyota, BMW, Ford…)",
          ar_model_ex: "مثال: كامري XLE", en_model_ex: "e.g. Camry XLE",
          ar_model_hint: "اسم الطراز أو الفئة — ليس سنة الصنع",
          en_model_hint: "The trim / model name — not the year",
          ar_desc_placeholder: "اكتب وصفاً موجزاً يبرز مميزات السيارة وحالتها العامة.",
          en_desc_placeholder: "Briefly describe the car's condition and selling points.",
        };
    }
  })();
  const nounDef = lang === "ar" ? typeCopy.ar_def : typeCopy.en_def;
  const nounYour = lang === "ar" ? typeCopy.ar_your : typeCopy.en_your;

  const estimate = async () => {
    setValBusy(true);
    try {
      const { data } = await apiClient.post("/ai/valuation", {
        make: form.make, model: form.model, year: Number(form.year),
        mileage_km: Number(form.mileage_km), condition: "good",
      });
      setValuation(data);
    } finally { setValBusy(false); }
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (form.images.length + files.length > MAX_IMAGES) {
      setErr(lang === "ar"
        ? `الحد الأقصى ${MAX_IMAGES} صور لكل إعلان.`
        : `Maximum ${MAX_IMAGES} images per listing.`);
      return;
    }
    setUploadBusy(true);
    setErr(null);
    try {
      const uploaded = [];
      for (const rawFile of files) {
        // iter317 — resize + re-encode to JPEG so 4G / HEIC uploads
        // don't hang past the axios 30s timeout.
        const file = await normalizeImageForUpload(rawFile);
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await apiClient.post("/upload?kind=vehicle", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          // Uploads on 4G still need more headroom than the shared
          // 30 s default even after resize (large albums, slow towers).
          timeout: 120000,
        });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (e) {
      // iter316 — surface the REAL error to the user.
      // `formatApiError(null|undefined)` returns the generic English
      // "Something went wrong…" fallback which was masking:
      //   • local `throw new Error("أضف صورة واحدة…")` messages
      //   • network errors where `e.response` is undefined
      // Both cases have no backend `detail`, so we only route through
      // formatApiError when there IS a backend detail, and prefer the
      // exception's own message otherwise.
      const detail = e.response?.data?.detail;
      setErr(detail ? formatApiError(detail) : (e.message || "Upload failed."));
    } finally {
      setUploadBusy(false);
      // reset the input so the same files can be re-selected if needed
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (form.images.length === 0) {
        throw new Error(lang === "ar" ? "أضف صورة واحدة على الأقل." : "Please upload at least one photo.");
      }
      if (!form.contact_phone && !form.contact_whatsapp && form.prefer_contact !== "chat") {
        throw new Error(lang === "ar"
          ? "أضف رقم جوال أو واتساب على الأقل."
          : "Provide at least a phone number or WhatsApp.");
      }
      // iter220 — enforce the description policy before submit (backend
      // re-validates so the user cannot bypass this by editing the payload).
      const _descCheck = validateDescription(form.description);
      if (!_descCheck.ok) {
        throw new Error(lang === "ar" ? _descCheck.message_ar : _descCheck.message_en);
      }
      const isAuction = form.listing_type === "auction";
      const payload = {
        ...form,
        year: Number(form.year),
        mileage_km: Number(form.mileage_km),
        seller_name: form.seller_name || user.name,
        image: form.images[0],
      };
      if (isAuction) {
        payload.starting_bid_sar = Number(form.starting_bid_sar);
        payload.reserve_price_sar = form.reserve_price_sar ? Number(form.reserve_price_sar) : null;
        payload.buy_now_price_sar = form.buy_now_price_sar ? Number(form.buy_now_price_sar) : null;
        payload.min_bid_increment_sar = Number(form.min_bid_increment_sar) || 500;
        payload.auction_duration_days = Number(form.auction_duration_days);
        payload.price_sar = Number(form.starting_bid_sar); // listing price = starting bid
      } else {
        payload.price_sar = Number(form.price_sar);
        // Fixed-price listings never use auction pricing fields — clear
        // them so the backend doesn't get "" on Optional[float] fields
        // (that was the exact source of the "Input should be a valid
        // number, unable to parse string as a number" error users saw).
        payload.starting_bid_sar = null;
        payload.reserve_price_sar = null;
        payload.buy_now_price_sar = null;
        payload.min_bid_increment_sar = null;
        payload.auction_duration_days = null;
      }
      // Final safety pass — any remaining empty-string on a numeric-
      // looking optional key (boat/moto/truck specs when the type
      // doesn't match) becomes null so Pydantic never sees "".
      const NUMERIC_HINT = /_(sar|km|tons|hp|hours|cc|m3|l|m|knots|cabins|berths|heads|capacity|cylinders|axles|days|count)$/;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" && NUMERIC_HINT.test(k)) payload[k] = null;
      });
      if (draft.draftId) payload.draft_id = draft.draftId;
      const { data } = await apiClient.post("/vehicles", payload);
      draft.clearLocal();
      nav(`/vehicle/${data.id}`, { state: { justPublished: true } });
    } catch (e) {
      // iter316 — same fix as uploadImages: prefer the exception's own
      // message when there's no backend detail, so local validation
      // throws (e.g. "أضف صورة واحدة على الأقل") are shown verbatim
      // instead of being swallowed by the generic English fallback.
      const detail = e.response?.data?.detail;
      setErr(detail ? formatApiError(detail) : (e.message || "Publish failed."));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="sell-page">
      <div className="overline mb-2">{lang === "ar" ? `أعلن عن ${nounYour}` : `LIST ${nounYour.toUpperCase()}`}</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">{lang === "ar" ? `بِع ${nounYour} على كارتال` : `Sell ${nounYour} on CARTAL`}</h1>
      <p className="text-[#6b6b6b] mt-2 max-w-2xl">{lang === "ar" ? "أنشئ إعلانك في دقائق — مع تقييم ذكي بالذكاء الاصطناعي." : "Create your listing in minutes — with an AI-powered price suggestion."}</p>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 mt-10">
        <form ref={formRef} onSubmit={submit} className="surface rounded-3xl p-8 space-y-5" data-testid="sell-form">
          <div className="flex items-center justify-between gap-2 text-xs text-[#6b6b6b]" data-testid="draft-status">
            <span>
              {draft.status === "saving" && (lang === "ar" ? "جارٍ حفظ المسودة…" : "Saving draft…")}
              {draft.status === "saved" && (lang === "ar" ? "تم حفظ المسودة تلقائياً" : "Draft saved automatically")}
              {draft.status === "offline" && (lang === "ar" ? "محفوظة على هذا الجهاز — ستُرفع عند عودة الاتصال" : "Saved on this device — will sync when back online")}
              {draft.status === "idle" && (lang === "ar" ? "يُحفظ تقدّمك تلقائياً كمسودة" : "Your progress is saved automatically as a draft")}
            </span>
            <button type="button" onClick={draft.saveNow} className="underline text-[#8a6f2e]" data-testid="draft-save-now">
              {lang === "ar" ? "حفظ الآن" : "Save now"}
            </button>
          </div>
          {/* Phase 2C — Post as Business selector (only when user owns at least one business) */}
          {myBusinesses.length > 0 && (
            <div className="rounded-2xl p-3"
                 style={{ background: "linear-gradient(135deg,rgba(232,200,115,0.10),rgba(201,165,92,0.03))",
                          border: "1px solid rgba(201,165,92,0.35)" }}
                 data-testid="sell-post-as">
              <div className="flex items-center gap-1.5 text-xs text-[#8a6f2e] font-bold uppercase tracking-widest mb-2">
                <Building2 className="w-3.5 h-3.5" /> {lang === "ar" ? "النشر باسم" : "Post as"}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {/* iter219 — hide the "بصفتي فرد" option for business
                    members; their account has no personal listing
                    identity and the backend refuses a submit with an
                    empty business_id. Owners and branch-managers keep
                    the toggle exactly as before. */}
                {!(user?.account_type === "member" || user?.role === "business_member") && (
                  <button type="button" onClick={() => { set("business_id", ""); set("branch_id", ""); }}
                          className="rounded-xl px-3 py-2 text-[12px] font-bold transition-all text-start"
                          style={!form.business_id
                            ? { background: "#0a0a0a", color: "#f7eecd" }
                            : { background: "transparent", color: "#525252", border: "1px solid rgba(10,10,10,0.15)" }}
                          data-testid="sell-post-as-individual">
                    {lang === "ar" ? "بصفتي فرد" : "As individual"}
                  </button>
                )}
                {myBusinesses.map((b) => (
                  <button key={b.id} type="button" onClick={() => set("business_id", b.id)}
                          className="rounded-xl px-3 py-2 text-[12px] font-bold transition-all text-start truncate"
                          style={form.business_id === b.id
                            ? { background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }
                            : { background: "transparent", color: "#525252", border: "1px solid rgba(10,10,10,0.15)" }}
                          data-testid={`sell-post-as-${b.id}`}>
                    <span className="truncate block">{lang === "ar" ? b.name_ar : (b.name_en || b.name_ar)}</span>
                  </button>
                ))}
              </div>
              {form.business_id && (
                <div className="text-[11px] text-[#8a6f2e] mt-2">
                  {lang === "ar"
                    ? "سيظهر الإعلان بشارة «مقدَّم من [اسم النشاط]» ورابط للملف العام."
                    : "The listing will show a 'By [business name]' badge with a link to the public profile."}
                </div>
              )}
            </div>
          )}

          {/* ── Sale mode toggle: Fixed price vs Auction (only shown when auctions are active) ── */}
          {FEATURES.AUCTIONS_ENABLED && (
          <div className="rounded-2xl p-3"
               style={{ background: "linear-gradient(135deg,rgba(201,165,92,0.08),rgba(10,10,10,0.04))",
                        border: "1px solid rgba(201,165,92,0.35)" }}
               data-testid="sell-mode-toggle">
            <div className="text-xs text-[#6b6b6b] mb-2">
              {lang === "ar" ? "كيف تريد البيع؟" : "How do you want to sell?"}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => set("listing_type", "fixed_price")}
                      data-testid="sell-mode-fixed"
                      className="rounded-xl px-3 py-2.5 text-sm font-bold transition-all"
                      style={form.listing_type === "fixed_price"
                        ? { background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }
                        : { background: "transparent", color: "#6b6b6b", border: "1px solid rgba(10,10,10,0.15)" }}>
                💰 {lang === "ar" ? "سعر ثابت" : "Fixed Price"}
                <div className="text-[10px] font-normal opacity-80 mt-0.5">
                  {lang === "ar" ? "تحدد السعر أنت — بدون مزايدة" : "You set the price — no bidding"}
                </div>
              </button>
              <button type="button" onClick={() => set("listing_type", "auction")}
                      data-testid="sell-mode-auction"
                      className="rounded-xl px-3 py-2.5 text-sm font-bold transition-all"
                      style={form.listing_type === "auction"
                        ? { background: "linear-gradient(135deg,#dc2626,#991b1b)", color: "#fff" }
                        : { background: "transparent", color: "#6b6b6b", border: "1px solid rgba(10,10,10,0.15)" }}>
                ⚖️ {lang === "ar" ? "مزاد" : "Auction"}
                <div className="text-[10px] font-normal opacity-80 mt-0.5">
                  {lang === "ar" ? "المشترون يزايدون — أعلى سعر يفوز" : "Buyers bid — highest wins"}
                </div>
              </button>
            </div>
          </div>
          )}

          {/* iter313 — Marine deal-type toggle: Sale vs. Rental. Only
              rendered for boat/yacht listings. The underlying payload
              stores `listing_type` = "fixed_price" | "rental" so the
              marketplace treats a rental as a rental listing without
              any schema change.
              iter326 — Reused verbatim for `type === "heavy"` so heavy
              equipment gets the exact same sale/rent semantics without
              a new backend field. Labels swap to heavy-equipment
              wording. */}
          {(form.type === "boat" || form.type === "heavy") && (
            <div className="rounded-2xl p-3"
                 style={{ background: "linear-gradient(135deg,rgba(14,116,144,0.10),rgba(10,10,10,0.04))",
                          border: "1px solid rgba(14,116,144,0.35)" }}
                 data-testid="sell-marine-deal-toggle">
              <div className="text-xs text-[#6b6b6b] mb-2">
                {lang === "ar" ? "نوع التعامل" : "Deal type"}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => set("listing_type", "fixed_price")}
                        data-testid="sell-marine-sale"
                        className="rounded-xl px-3 py-2.5 text-sm font-bold transition-all"
                        style={form.listing_type === "fixed_price"
                          ? { background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }
                          : { background: "transparent", color: "#6b6b6b", border: "1px solid rgba(10,10,10,0.15)" }}>
                  💰 {lang === "ar" ? "للبيع" : "Sale"}
                  <div className="text-[10px] font-normal opacity-80 mt-0.5">
                    {form.type === "heavy"
                      ? (lang === "ar" ? "بيع المعدة بسعر ثابت" : "Sell the equipment at a fixed price")
                      : (lang === "ar" ? "بيع اليخت / الزورق بسعر ثابت" : "Sell the yacht/boat at a fixed price")}
                  </div>
                </button>
                <button type="button" onClick={() => set("listing_type", "rental")}
                        data-testid="sell-marine-rental"
                        className="rounded-xl px-3 py-2.5 text-sm font-bold transition-all"
                        style={form.listing_type === "rental"
                          ? { background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "#fff" }
                          : { background: "transparent", color: "#6b6b6b", border: "1px solid rgba(10,10,10,0.15)" }}>
                  {form.type === "heavy" ? "🏗️" : "⚓"} {lang === "ar" ? "للإيجار" : "Rental"}
                  <div className="text-[10px] font-normal opacity-80 mt-0.5">
                    {form.type === "heavy"
                      ? (lang === "ar" ? "يومي / أسبوعي / شهري / سنوي / حسب الطلب" : "Daily / weekly / monthly / yearly / on demand")
                      : (lang === "ar" ? "تأجير بالساعة / اليوم / الرحلة" : "Rent by hour / day / trip")}
                  </div>
                </button>
              </div>
              {form.listing_type === "rental" && (
                <>
                  {/* iter326 — Rental period selector (heavy equipment only).
                      Persists on the existing `rental_period` field
                      (`daily` | `weekly` | `monthly` | `yearly` |
                      `on_demand`) — same field the car-rental flow
                      already reads. No new backend column. */}
                  {form.type === "heavy" && (
                    <div className="mt-3 grid grid-cols-5 gap-1.5" data-testid="sell-heavy-rental-period">
                      {[
                        ["daily",    "يومي",     "Daily"],
                        ["weekly",   "أسبوعي",   "Weekly"],
                        ["monthly",  "شهري",     "Monthly"],
                        ["yearly",   "سنوي",     "Yearly"],
                        ["on_demand","حسب الطلب","On demand"],
                      ].map(([k, ar, en]) => (
                        <button
                          type="button"
                          key={k}
                          onClick={() => set("rental_period", k)}
                          className="rounded-lg px-1.5 py-2 text-[11px] font-bold transition-all"
                          style={form.rental_period === k
                            ? { background: "#0e7490", color: "#fff" }
                            : { background: "transparent", color: "#6b6b6b", border: "1px solid rgba(10,10,10,0.15)" }}
                          data-testid={`sell-heavy-rental-${k}`}
                        >
                          {lang === "ar" ? ar : en}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-[#0e7490] mt-2 leading-relaxed">
                    {lang === "ar"
                      ? "إعلان التأجير لا يشترط سعرًا ظاهرًا؛ يمكن للمهتمين التواصل معك عبر بيانات النشاط لتحديد شروط الإيجار."
                      : "Rental listings don't require a public price; interested renters contact you via your activity to agree on terms."}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع الإعلان" : "Listing type"}</label>
              <select className="input-dark mt-1" value={form.type} onChange={(e) => {
                const newType = e.target.value;
                setForm((f) => ({
                  ...f,
                  type: newType,
                  // Reset body to a valid default for the new vehicle category so
                  // users don't end up with "Sedan" selected on a motorcycle listing.
                  body: newType === "motorcycle" ? "Sport"
                      : newType === "heavy"      ? "Tractor"
                      : newType === "boat"       ? f.body
                      : "Sedan",
                }));
              }}>
                {VEHICLE_TYPES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "المدينة" : "City"}</label>
              <select className="input-dark mt-1" required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="sell-city">
                {CITIES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
              </select>
              <CitySearchBox lang={lang} value={form.city} onSelect={(id) => set("city", id)} />
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.make")}</label>
              <input
                className="input-dark mt-1"
                required
                value={form.make}
                onChange={(e) => set("make", e.target.value)}
                placeholder={lang === "ar" ? typeCopy.ar_make_ex : typeCopy.en_make_ex}
                data-testid="sell-make"
              />
              <div className="text-[10px] text-[#8a8a8a] mt-1">
                {lang === "ar" ? typeCopy.ar_make_hint : typeCopy.en_make_hint}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.model")}</label>
              <input
                className="input-dark mt-1"
                required
                value={form.model}
                onChange={(e) => set("model", e.target.value)}
                placeholder={lang === "ar" ? typeCopy.ar_model_ex : typeCopy.en_model_ex}
                data-testid="sell-model"
              />
              <div className="text-[10px] text-[#8a8a8a] mt-1">
                {lang === "ar" ? typeCopy.ar_model_hint : typeCopy.en_model_hint}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.year")}</label>
              <input
                type="number"
                className="input-dark mt-1"
                required
                value={form.year}
                onChange={(e) => set("year", e.target.value)}
                placeholder="2023"
                data-testid="sell-year"
              />
              <div className="text-[10px] text-[#8a8a8a] mt-1">
                {form.type === "boat"
                  ? (lang === "ar" ? "سنة تصنيع اليخت / الزورق" : "Year the boat was manufactured")
                  : form.type === "motorcycle"
                    ? (lang === "ar" ? "سنة تصنيع الدراجة" : "Year the motorcycle was manufactured")
                    : form.type === "heavy"
                      ? (lang === "ar" ? "سنة تصنيع الشاحنة" : "Year the truck was manufactured")
                      : (lang === "ar" ? "سنة تصنيع السيارة (الموديل)" : "Manufacturing year (commonly called 'موديل')")}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">
                {form.type === "boat"
                  ? (lang === "ar" ? "ساعات المحرك (Engine hours)" : "Engine hours")
                  : (lang === "ar" ? "العدّاد (كم)" : "Mileage (km)")}
              </label>
              <input type="number" className="input-dark mt-1"
                     value={form.type === "boat" ? form.boat_engine_hours : form.mileage_km}
                     onChange={(e) => set(form.type === "boat" ? "boat_engine_hours" : "mileage_km", e.target.value)}
                     data-testid="sell-mileage" />
              {form.type === "boat" && (
                <div className="text-[10px] text-[#8a8a8a] mt-1">
                  {lang === "ar" ? "المعيار الدولي لعمر الاستخدام في القوارب — بديل عن الكيلومترات" : "International standard for boat usage — replaces mileage"}
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              {form.listing_type === "fixed_price" ? (
                <>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "السعر (ريال)" : "Price (SAR)"}</label>
                  <input type="number" className="input-dark mt-1" required value={form.price_sar} onChange={(e) => set("price_sar", e.target.value)} data-testid="sell-price" />

                  {/* Phase 2A — Price visibility (Marketplace-wide feature) */}
                  <div className="mt-3 rounded-xl border border-[#0a0a0a]/[0.08] bg-[#fafaf7] p-3" data-testid="sell-price-visibility">
                    <div className="text-[12px] font-bold text-[#0a0a0a] mb-2">
                      {lang === "ar" ? "طريقة عرض السعر" : "Price display"}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "shown", ar: "إظهار السعر", en: "Show price", desc_ar: "يظهر السعر مباشرة", desc_en: "Visible to all" },
                        { id: "hidden", ar: "إخفاء السعر واستقبال العروض", en: "Hide price · get offers", desc_ar: "المهتم يقدّم عرضًا خاصًا", desc_en: "Buyers submit private offers" },
                      ].map((opt) => {
                        const active = form.price_visibility === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => set("price_visibility", opt.id)}
                            className={`text-start rounded-xl p-3 border transition-all ${
                              active
                                ? "border-[#c9a55c] bg-white shadow-sm"
                                : "border-[#0a0a0a]/10 bg-white hover:border-[#c9a55c]/40"
                            }`}
                            data-testid={`sell-price-visibility-${opt.id}`}
                          >
                            <div className="text-[12px] font-semibold text-[#0a0a0a]">
                              {lang === "ar" ? opt.ar : opt.en}
                            </div>
                            <div className="text-[10px] text-[#8a8a8a] mt-0.5">
                              {lang === "ar" ? opt.desc_ar : opt.desc_en}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {form.price_visibility === "hidden" && (
                      <div className="mt-2 text-[11px] text-[#8a6f2e] bg-[#fef7e6] rounded-lg px-3 py-1.5 border border-[#c9a55c]/25">
                        {lang === "ar"
                          ? "لن يظهر السعر للمشترين. سيصلك العرض خاصًا داخل لوحة إعلانك."
                          : "The price stays hidden. Offers arrive privately in your listing panel."}
                      </div>
                    )}
                  </div>

                  {/* iter247 — optional sale-method chips (0..4). Sits
                      just below the cash-price block; ad detail page
                      renders selected chips inline, no extra title. */}
                  <div className="mt-3">
                    <SaleOptionsChips
                      value={form.sale_options}
                      onChange={(next) => set("sale_options", next)}
                      lang={lang}
                    />
                  </div>
                </>
              ) : (
                <div className="rounded-2xl p-4" style={{ background: "linear-gradient(135deg,rgba(220,38,38,0.06),rgba(201,165,92,0.08))", border: "1px solid rgba(220,38,38,0.35)" }} data-testid="sell-auction-block">
                  <div className="font-bold text-[#0a0a0a] mb-3 flex items-center gap-2">
                    <span className="text-lg" role="img">⚖️</span>
                    {lang === "ar" ? "إعدادات المزاد" : "Auction settings"}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "سعر البدء (ر.س) *" : "Starting bid (SAR) *"}</label>
                      <input type="number" min="1" className="input-dark mt-1" required value={form.starting_bid_sar} onChange={(e) => set("starting_bid_sar", e.target.value)} data-testid="sell-starting-bid" />
                      <div className="text-[10px] text-[#8a8a8a] mt-1">
                        {lang === "ar" ? "أقل مبلغ يبدأ منه المزاد" : "Minimum bid to open the auction"}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "مدة المزاد" : "Auction duration"}</label>
                      <select className="input-dark mt-1" value={form.auction_duration_days} onChange={(e) => set("auction_duration_days", Number(e.target.value))} data-testid="sell-auction-duration">
                        <option value={3}>{lang === "ar" ? "3 أيام" : "3 days"}</option>
                        <option value={5}>{lang === "ar" ? "5 أيام" : "5 days"}</option>
                        <option value={7}>{lang === "ar" ? "7 أيام" : "7 days"}</option>
                        <option value={14}>{lang === "ar" ? "14 يوم" : "14 days"}</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#6b6b6b]">
                        {lang === "ar" ? "الحد الأدنى للزيادة بين المزايدات (ر.س)" : "Min bid increment (SAR)"}
                      </label>
                      <input type="number" min="100" className="input-dark mt-1" value={form.min_bid_increment_sar} onChange={(e) => set("min_bid_increment_sar", e.target.value)} data-testid="sell-bid-increment" />
                      <div className="text-[10px] text-[#8a8a8a] mt-1">
                        {lang === "ar" ? "كل مزايدة جديدة يجب أن تكون أعلى بهذا المقدار على الأقل" : "Each new bid must exceed the previous one by at least this much"}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#6b6b6b] inline-flex items-center gap-1">
                        {lang === "ar" ? "سعر احتياطي (سرّي)" : "Reserve price (secret)"}
                        <span className="text-[9px] text-[#8a8a8a]">— {lang === "ar" ? "اختياري" : "optional"}</span>
                      </label>
                      <input type="number" min="0" className="input-dark mt-1" placeholder={lang === "ar" ? "اتركه فارغاً إن لم تُرد" : "Leave blank if none"} value={form.reserve_price_sar} onChange={(e) => set("reserve_price_sar", e.target.value)} data-testid="sell-reserve-price" />
                      <div className="text-[10px] text-[#8a8a8a] mt-1">
                        {lang === "ar" ? "إن لم يصل المزاد إلى هذا المبلغ، لن تُلزم بالبيع" : "If bidding doesn't reach this, you're not obligated to sell"}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-[#6b6b6b] inline-flex items-center gap-1">
                        {lang === "ar" ? "\"اشترِ الآن\" (سعر إنهاء فوري)" : "\"Buy Now\" (instant-win price)"}
                        <span className="text-[9px] text-[#8a8a8a]">— {lang === "ar" ? "اختياري" : "optional"}</span>
                      </label>
                      <input type="number" min="0" className="input-dark mt-1" placeholder={lang === "ar" ? "مثال: 120000" : "e.g. 120000"} value={form.buy_now_price_sar} onChange={(e) => set("buy_now_price_sar", e.target.value)} data-testid="sell-buy-now-price" />
                      <div className="text-[10px] text-[#8a8a8a] mt-1">
                        {lang === "ar" ? "أي مزايد يدفع هذا المبلغ يفوز فوراً وينتهي المزاد" : "Any bidder matching this price wins instantly and ends the auction"}
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-[#6b6b6b] mt-3 leading-relaxed">
                    {lang === "ar"
                      ? "المزاد يبدأ فور نشر إعلانك وينتهي بعد المدة المحددة. الفائز يُشعَر تلقائياً ولديه 24 ساعة لإتمام الدفع."
                      : "The auction opens immediately after publishing and closes after the selected duration. The winner is notified automatically and has 24 hours to settle."}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">
                {form.type === "motorcycle"
                  ? (lang === "ar" ? "فئة الدراجة" : "Motorcycle style")
                  : form.type === "heavy"
                    ? (lang === "ar" ? "نوع الشاحنة" : "Truck type")
                    : (lang === "ar" ? "نوع الهيكل" : "Body type")}
              </label>
              <select className="input-dark mt-1" value={form.body} onChange={(e) => set("body", e.target.value)}
                      disabled={form.type === "boat"}
                      data-testid="sell-body">
                {(form.type === "motorcycle" ? MOTORCYCLE_BODY_TYPES
                  : form.type === "heavy" ? HEAVY_BODY_TYPES
                  : BODY_TYPES).map(([id, en, ar]) => (
                    <option key={id} value={id}>{lang === "ar" ? ar : en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "ناقل الحركة" : "Transmission"}</label>
              <select className="input-dark mt-1" value={form.transmission} onChange={(e) => set("transmission", e.target.value)}
                      disabled={form.type === "boat"}
                      data-testid="sell-transmission">
                {TRANSMISSIONS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع الوقود" : "Fuel"}</label>
              <select className="input-dark mt-1" value={form.fuel} onChange={(e) => set("fuel", e.target.value)}
                      disabled={form.type === "boat"}
                      data-testid="sell-fuel">
                {FUELS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
              </select>
            </div>
          </div>

          {/* ── Boat / Yacht specifications (only when type === "boat") ── */}
          {form.type === "boat" && (
            <div className="rounded-2xl p-4 mt-2 space-y-3"
                 style={{ background: "linear-gradient(135deg,rgba(14,165,233,0.06),rgba(201,165,92,0.06))",
                          border: "1px solid rgba(14,165,233,0.35)" }}
                 data-testid="sell-boat-block">
              <div className="font-bold text-[#0a0a0a] flex items-center gap-2">
                <span role="img" aria-label="boat">⛵</span>
                {lang === "ar" ? "مواصفات اليخت / الزورق (المعيار الدولي)" : "Yacht / Boat specifications (international standard)"}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع القارب" : "Boat type"}</label>
                  <select className="input-dark mt-1" value={form.boat_type} onChange={(e) => set("boat_type", e.target.value)} data-testid="sell-boat-type">
                    {BOAT_TYPES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الحالة" : "Condition"}</label>
                  <select className="input-dark mt-1" value={form.boat_condition} onChange={(e) => set("boat_condition", e.target.value)} data-testid="sell-boat-condition">
                    {BOAT_CONDITIONS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الطول الكلي LOA (متر)" : "Length Overall — LOA (m)"}</label>
                  <input type="number" step="0.1" className="input-dark mt-1" value={form.boat_length_m}
                         onChange={(e) => set("boat_length_m", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 24.5" : "e.g. 24.5"}
                         data-testid="sell-boat-loa" />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">
                    {lang === "ar" ? "الطول من الحافة إلى الحافة" : "Bow-to-stern total length"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "العرض Beam (متر)" : "Beam (m)"}</label>
                  <input type="number" step="0.1" className="input-dark mt-1" value={form.boat_beam_m}
                         onChange={(e) => set("boat_beam_m", e.target.value)}
                         data-testid="sell-boat-beam" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الغاطس Draft (متر)" : "Draft (m)"}</label>
                  <input type="number" step="0.1" className="input-dark mt-1" value={form.boat_draft_m}
                         onChange={(e) => set("boat_draft_m", e.target.value)}
                         data-testid="sell-boat-draft" />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">
                    {lang === "ar" ? "عمق الجزء المغمور تحت الماء" : "Depth of hull below waterline"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "خامة البدن" : "Hull material"}</label>
                  <select className="input-dark mt-1" value={form.boat_hull_material} onChange={(e) => set("boat_hull_material", e.target.value)} data-testid="sell-boat-hull">
                    {BOAT_HULLS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع المحرك" : "Engine type"}</label>
                  <select className="input-dark mt-1" value={form.boat_engine_type} onChange={(e) => set("boat_engine_type", e.target.value)} data-testid="sell-boat-engine-type">
                    {BOAT_ENGINES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع الوقود" : "Fuel type"}</label>
                  <select className="input-dark mt-1" value={form.boat_fuel_type} onChange={(e) => set("boat_fuel_type", e.target.value)} data-testid="sell-boat-fuel">
                    {BOAT_FUELS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "عدد المحركات" : "Number of engines"}</label>
                  <select className="input-dark mt-1" value={form.boat_engines_count} onChange={(e) => set("boat_engines_count", Number(e.target.value))} data-testid="sell-boat-engines-count">
                    {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "قوة المحرك (حصان)" : "Engine power (HP)"}</label>
                  <input type="number" className="input-dark mt-1" value={form.boat_engine_power_hp}
                         onChange={(e) => set("boat_engine_power_hp", e.target.value)}
                         placeholder={lang === "ar" ? "لكل محرك أو الإجمالي" : "per engine or total"}
                         data-testid="sell-boat-hp" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "السرعة القصوى (عقدة)" : "Max speed (knots)"}</label>
                  <input type="number" step="0.1" className="input-dark mt-1" value={form.boat_max_speed_knots}
                         onChange={(e) => set("boat_max_speed_knots", e.target.value)}
                         data-testid="sell-boat-speed" />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">
                    {lang === "ar" ? "1 عقدة ≈ 1.852 كم/س" : "1 knot ≈ 1.852 km/h"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "عدد الركاب" : "Passenger capacity"}</label>
                  <input type="number" className="input-dark mt-1" value={form.boat_capacity}
                         onChange={(e) => set("boat_capacity", e.target.value)}
                         data-testid="sell-boat-capacity" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الكبائن" : "Cabins"}</label>
                  <input type="number" min="0" className="input-dark mt-1" value={form.boat_cabins}
                         onChange={(e) => set("boat_cabins", e.target.value)}
                         data-testid="sell-boat-cabins" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "أَسِرّة النوم (Berths)" : "Berths (sleeping spots)"}</label>
                  <input type="number" min="0" className="input-dark mt-1" value={form.boat_berths}
                         onChange={(e) => set("boat_berths", e.target.value)}
                         data-testid="sell-boat-berths" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الحمامات (Heads)" : "Heads (bathrooms)"}</label>
                  <input type="number" min="0" className="input-dark mt-1" value={form.boat_heads}
                         onChange={(e) => set("boat_heads", e.target.value)}
                         data-testid="sell-boat-heads" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "سعة الوقود (لتر)" : "Fuel capacity (L)"}</label>
                  <input type="number" className="input-dark mt-1" value={form.boat_fuel_capacity_l}
                         onChange={(e) => set("boat_fuel_capacity_l", e.target.value)}
                         data-testid="sell-boat-fuel-cap" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "سعة المياه العذبة (لتر)" : "Fresh water capacity (L)"}</label>
                  <input type="number" className="input-dark mt-1" value={form.boat_water_capacity_l}
                         onChange={(e) => set("boat_water_capacity_l", e.target.value)}
                         data-testid="sell-boat-water-cap" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "دولة العلم / التسجيل" : "Flag / Country of registration"}</label>
                  <input type="text" className="input-dark mt-1" value={form.boat_flag}
                         onChange={(e) => set("boat_flag", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: السعودية، جزر كايمان، مالطا" : "e.g. Saudi Arabia, Cayman Islands, Malta"}
                         data-testid="sell-boat-flag" />
                </div>
              </div>
              <div className="text-[11px] text-[#6b6b6b] leading-relaxed border-t border-[#0a0a0a]/[0.08] pt-2 mt-2">
                {lang === "ar"
                  ? "الحقول الأساسية (LOA + Beam + نوع القارب + المحرك) تكفي لأغلب الإعلانات — البقية اختياري."
                  : "Only Type + LOA + Beam + Engine are required — the rest is optional."}
              </div>
            </div>
          )}

          {/* ── Motorcycle specifications (only when type === "motorcycle") ── */}
          {form.type === "motorcycle" && (
            <div className="rounded-2xl p-4 mt-2 space-y-3"
                 style={{ background: "linear-gradient(135deg,rgba(220,38,38,0.05),rgba(201,165,92,0.06))",
                          border: "1px solid rgba(220,38,38,0.3)" }}
                 data-testid="sell-moto-block">
              <div className="font-bold text-[#0a0a0a] flex items-center gap-2">
                <span role="img" aria-label="motorcycle">🏍️</span>
                {lang === "ar" ? "مواصفات الدراجة" : "Motorcycle specifications"}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "سعة المحرك (CC) *" : "Engine capacity — CC *"}
                  </label>
                  <input type="number" min="0" className="input-dark mt-1" required
                         value={form.moto_engine_cc}
                         onChange={(e) => set("moto_engine_cc", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 600" : "e.g. 600"}
                         data-testid="sell-moto-cc" />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">
                    {lang === "ar" ? "بالسنتيمتر المكعّب (cc) — المعيار المتعارف عليه" : "In cubic centimeters (cc) — the industry standard"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "عدد الأسطوانات" : "Cylinders"}
                  </label>
                  <select className="input-dark mt-1" value={form.moto_cylinders}
                          onChange={(e) => set("moto_cylinders", e.target.value)}
                          data-testid="sell-moto-cyl">
                    <option value="">{lang === "ar" ? "غير محدد" : "Not specified"}</option>
                    <option value="1">1 (Single)</option>
                    <option value="2">2 (Twin / V-Twin)</option>
                    <option value="3">3 (Triple)</option>
                    <option value="4">4 (Inline-4 / V4)</option>
                    <option value="6">6 (Inline-6 / Flat-6)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "نظام التبريد" : "Cooling"}
                  </label>
                  <select className="input-dark mt-1" value={form.moto_cooling}
                          onChange={(e) => set("moto_cooling", e.target.value)}
                          data-testid="sell-moto-cooling">
                    <option value="liquid">{lang === "ar" ? "سائل" : "Liquid-cooled"}</option>
                    <option value="air">{lang === "ar" ? "هوائي" : "Air-cooled"}</option>
                    <option value="oil">{lang === "ar" ? "زيتي" : "Oil-cooled"}</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-[#0a0a0a] font-medium cursor-pointer select-none">
                    <input type="checkbox" className="w-4 h-4 accent-[#8a6f2e]"
                           checked={form.moto_abs}
                           onChange={(e) => set("moto_abs", e.target.checked)}
                           data-testid="sell-moto-abs" />
                    {lang === "ar" ? "مزوّدة بنظام ABS للفرامل" : "Equipped with ABS brakes"}
                  </label>
                </div>
              </div>
              <div className="text-[11px] text-[#6b6b6b] mt-2 leading-relaxed">
                {lang === "ar"
                  ? "السعة اللترية (CC) هي المعيار الأول الذي يبحث عنه المشتري — تأكد من صحتها."
                  : "CC displacement is the top spec riders search for — please double-check."}
              </div>
            </div>
          )}

          {/* ── Heavy truck specifications (only when type === "heavy") ── */}
          {form.type === "heavy" && (
            <div className="rounded-2xl p-4 mt-2 space-y-3"
                 style={{ background: "linear-gradient(135deg,rgba(10,10,10,0.04),rgba(201,165,92,0.08))",
                          border: "1px solid rgba(10,10,10,0.25)" }}
                 data-testid="sell-truck-block">
              <div className="font-bold text-[#0a0a0a] flex items-center gap-2">
                <span role="img" aria-label="truck">🚛</span>
                {lang === "ar" ? "مواصفات الشاحنة" : "Truck specifications"}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "الحمولة (طن) *" : "Payload capacity — tonnes *"}
                  </label>
                  <input type="number" min="0" step="0.1" className="input-dark mt-1" required
                         value={form.truck_payload_tons}
                         onChange={(e) => set("truck_payload_tons", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 12" : "e.g. 12"}
                         data-testid="sell-truck-payload" />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">
                    {lang === "ar" ? "أقصى وزن يمكن للشاحنة نقله" : "Maximum weight the truck can carry"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "الوزن الإجمالي GVW (طن)" : "Gross Vehicle Weight — GVW (t)"}
                  </label>
                  <input type="number" min="0" step="0.1" className="input-dark mt-1"
                         value={form.truck_gvw_tons}
                         onChange={(e) => set("truck_gvw_tons", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 26" : "e.g. 26"}
                         data-testid="sell-truck-gvw" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "عدد المحاور" : "Axles"}
                  </label>
                  <select className="input-dark mt-1" value={form.truck_axles}
                          onChange={(e) => set("truck_axles", Number(e.target.value))}
                          data-testid="sell-truck-axles">
                    <option value={2}>2 (4x2)</option>
                    <option value={3}>3 (6x2 / 6x4)</option>
                    <option value={4}>4 (8x4)</option>
                    <option value={5}>5</option>
                    <option value={6}>6+</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "سعة الصندوق (م³)" : "Cargo volume (m³)"}
                  </label>
                  <input type="number" min="0" step="0.1" className="input-dark mt-1"
                         value={form.truck_cargo_m3}
                         onChange={(e) => set("truck_cargo_m3", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 45" : "e.g. 45"}
                         data-testid="sell-truck-cargo" />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">
                    {lang === "ar" ? "اختياري — يفيد للبراد والصناديق المغلقة" : "Optional — useful for refrigerated/box trucks"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "سعة المحرك (لتر)" : "Engine displacement (L)"}
                  </label>
                  <input type="number" min="0" step="0.1" className="input-dark mt-1"
                         value={form.truck_engine_l}
                         onChange={(e) => set("truck_engine_l", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 12.7" : "e.g. 12.7"}
                         data-testid="sell-truck-engine-l" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "قوة المحرك (حصان)" : "Engine power (HP)"}
                  </label>
                  <input type="number" min="0" className="input-dark mt-1"
                         value={form.truck_engine_hp}
                         onChange={(e) => set("truck_engine_hp", e.target.value)}
                         placeholder={lang === "ar" ? "مثال: 460" : "e.g. 460"}
                         data-testid="sell-truck-engine-hp" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? "نوع الكابينة" : "Cab type"}
                  </label>
                  <select className="input-dark mt-1" value={form.truck_cab_type}
                          onChange={(e) => set("truck_cab_type", e.target.value)}
                          data-testid="sell-truck-cab">
                    <option value="day">{lang === "ar" ? "يومية (Day cab)" : "Day cab"}</option>
                    <option value="sleeper">{lang === "ar" ? "بمبيت (Sleeper)" : "Sleeper cab"}</option>
                    <option value="crew">{lang === "ar" ? "طاقم مزدوجة (Crew cab)" : "Crew cab"}</option>
                    <option value="cabover">{lang === "ar" ? "كابوفر (فوق المحرك)" : "Cab-over-engine"}</option>
                  </select>
                </div>
              </div>
              <div className="text-[11px] text-[#6b6b6b] mt-2 leading-relaxed">
                {lang === "ar"
                  ? "الحمولة وعدد المحاور هما أهم ما يبحث عنه مشترو الشاحنات — تأكد من دقتهما."
                  : "Payload capacity and axle count are the top filters buyers use — please be accurate."}
              </div>
            </div>
          )}

          {/* iter327 — Dynamic heavy-equipment specs (Phase 2).
              Fields are configured by the Super Admin per type_key
              (form.body). Renders ONLY when specs exist so the SellPage is
              unchanged for types without any admin-defined fields. */}
          {form.type === "heavy" && heavyDynamicSpecs.length > 0 && (
            <div className="rounded-2xl p-4 mt-2 space-y-3"
                 style={{ background: "linear-gradient(135deg,rgba(10,10,10,0.04),rgba(201,165,92,0.08))",
                          border: "1px solid rgba(10,10,10,0.25)" }}
                 data-testid="sell-heavy-dynamic-specs">
              <div className="font-bold text-[#0a0a0a] flex items-center gap-2">
                <span role="img" aria-label="wrench">🛠️</span>
                {lang === "ar" ? "مواصفات إضافية لهذا النوع" : "Additional specs for this type"}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {heavyDynamicSpecs.map((s) => {
                  const label = (lang === "ar" ? s.label_ar : s.label_en) || s.key;
                  const unit = (lang === "ar" ? s.unit_ar : s.unit_en) || "";
                  const placeholder = (lang === "ar" ? s.placeholder_ar : s.placeholder_en) || "";
                  const val = (form.heavy_specs && form.heavy_specs[s.key]) ?? "";
                  const setVal = (v) =>
                    setForm((f) => ({
                      ...f,
                      heavy_specs: { ...(f.heavy_specs || {}), [s.key]: v },
                    }));
                  const testid = `sell-heavy-dyn-${s.key}`;
                  return (
                    <div key={s.id} className={s.field_type === "boolean" ? "sm:col-span-2" : ""}>
                      <label className="text-xs text-[#6b6b6b]">
                        {label}{unit ? ` (${unit})` : ""}{s.required ? " *" : ""}
                      </label>
                      {s.field_type === "number" && (
                        <input
                          type="number"
                          className="input-dark mt-1"
                          value={val}
                          placeholder={placeholder}
                          required={!!s.required}
                          onChange={(e) => setVal(e.target.value)}
                          data-testid={testid}
                        />
                      )}
                      {s.field_type === "text" && (
                        <input
                          type="text"
                          className="input-dark mt-1"
                          value={val}
                          placeholder={placeholder}
                          required={!!s.required}
                          onChange={(e) => setVal(e.target.value)}
                          data-testid={testid}
                        />
                      )}
                      {s.field_type === "select" && (
                        <select
                          className="input-dark mt-1"
                          value={val}
                          required={!!s.required}
                          onChange={(e) => setVal(e.target.value)}
                          data-testid={testid}
                        >
                          <option value="">
                            {lang === "ar" ? "— اختر —" : "— Select —"}
                          </option>
                          {(s.options || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                      {s.field_type === "boolean" && (
                        <label className="flex items-center gap-2 mt-1 text-sm">
                          <input
                            type="checkbox"
                            checked={val === true || val === "true"}
                            onChange={(e) => setVal(e.target.checked)}
                            data-testid={testid}
                          />
                          <span>{lang === "ar" ? "نعم" : "Yes"}</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-[#6b6b6b] mt-1">
                {lang === "ar"
                  ? "هذه الحقول تخص هذا النوع فقط ويديرها فريق الإدارة."
                  : "These fields are specific to this type and are managed by the admin team."}
              </div>
            </div>
          )}

          {!["motorcycle", "heavy", "marine", "boat", "salvage"].includes(form.type) && (
            <SpecChecklist value={form.specs} onChange={(s) => set("specs", s)} lang={lang} />
          )}

          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الوصف" : "Description"}</label>
            <textarea
              className="input-dark mt-1"
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value.slice(0, DESCRIPTION_MAX_LEN))}
              maxLength={DESCRIPTION_MAX_LEN}
              placeholder={lang === "ar" ? typeCopy.ar_desc_placeholder : typeCopy.en_desc_placeholder}
              data-testid="sell-description"
            />
            {/* iter220 — live character counter + policy warning */}
            <div className="flex items-center justify-between text-[11px] mt-1">
              {(() => {
                const check = validateDescription(form.description);
                return (
                  <span
                    className={check.ok ? "text-[#8a8a8a]" : "text-red-500"}
                    data-testid="sell-description-hint"
                  >
                    {check.ok ? "" : (lang === "ar" ? check.message_ar : check.message_en)}
                  </span>
                );
              })()}
              <span
                className={((form.description || "").length >= DESCRIPTION_MAX_LEN ? "text-red-500 font-semibold" : "text-[#8a8a8a]")}
                data-testid="sell-description-counter"
              >
                {(form.description || "").length} / {DESCRIPTION_MAX_LEN}
              </span>
            </div>
          </div>

          {/* ── Photos (multi-upload) ───────────────────────────────────── */}
          <div data-testid="sell-images-section">
            <label className="text-xs text-[#6b6b6b] block mb-2">
              {lang === "ar" ? `صور ${nounDef} (حتى ${MAX_IMAGES})` : `${nounDef.charAt(0).toUpperCase() + nounDef.slice(1)} photos (up to ${MAX_IMAGES})`}
              <span className="text-red-600"> *</span>
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {form.images.map((url, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-[#0a0a0a]/15 group" data-testid={`sell-image-${i}`}>
                  <img src={url} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute top-1 start-1 text-[9px] font-semibold uppercase tracking-wider bg-[#c9a55c] text-[#0a0a0a] px-1.5 py-0.5 rounded">
                      {lang === "ar" ? "غلاف" : "Cover"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 end-1 w-6 h-6 rounded-full bg-[#0a0a0a]/70 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                    data-testid={`sell-image-remove-${i}`}
                    aria-label="Remove image"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {form.images.length < MAX_IMAGES && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-[#0a0a0a]/20 hover:border-[#c9a55c] flex flex-col items-center justify-center cursor-pointer text-center text-[10px] text-[#6b6b6b] px-2 transition-colors" data-testid="sell-upload-tile">
                  {uploadBusy ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[#8a6f2e]" />
                  ) : (
                    <>
                      <UploadIcon className="w-5 h-5 text-[#8a6f2e] mb-1" />
                      <span>{lang === "ar" ? "أضف صور" : "Add photos"}</span>
                    </>
                  )}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} data-testid="sell-upload" />
                </label>
              )}
            </div>
            <p className="text-[11px] text-[#6b6b6b]">
              {lang === "ar"
                ? "أول صورة هي صورة الغلاف. يمكنك سحب وإفلات أو رفع عدة صور دفعة واحدة."
                : "First photo is the cover. You can upload several at once."}
            </p>
          </div>

          {/* ── Contact preferences (NEW) ───────────────────────────────── */}
          <div className="rounded-2xl border border-[#0a0a0a]/10 p-5 bg-[#fafaf7]" data-testid="sell-contact-section">
            <div className="overline overline-gold mb-3">{lang === "ar" ? "كيف يتواصل معك المشتري؟" : "How buyers reach you"}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6b6b6b] inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {lang === "ar" ? "رقم الجوال" : "Phone number"} {form.prefer_contact !== "chat" && <span className="text-red-600">*</span>}</label>
                <input
                  type="tel"
                  className="input-dark mt-1"
                  placeholder="+966 5x xxx xxxx"
                  value={form.contact_phone}
                  onChange={(e) => set("contact_phone", e.target.value)}
                  data-testid="sell-contact-phone"
                />
              </div>
              <div>
                <label className="text-xs text-[#6b6b6b] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {lang === "ar" ? "واتساب (اختياري)" : "WhatsApp (optional)"}</label>
                <input
                  type="tel"
                  className="input-dark mt-1"
                  placeholder={lang === "ar" ? "اتركه فارغاً ليطابق رقم الجوال" : "Leave empty to use phone"}
                  value={form.contact_whatsapp}
                  onChange={(e) => set("contact_whatsapp", e.target.value)}
                  data-testid="sell-contact-whatsapp"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "طريقة التواصل المفضّلة" : "Preferred contact method"}</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { id: "phone", ar: "اتصال", en: "Call" },
                    { id: "whatsapp", ar: "واتساب", en: "WhatsApp" },
                    { id: "chat", ar: "رسائل كارتال", en: "CARTAL chat" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => set("prefer_contact", opt.id)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold border-2 transition-colors ${
                        form.prefer_contact === opt.id
                          ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                          : "bg-white text-[#3a3a3a] border-[#0a0a0a]/15 hover:border-[#c9a55c]"
                      }`}
                      data-testid={`sell-prefer-${opt.id}`}
                    >
                      {lang === "ar" ? opt.ar : opt.en}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Periodic inspection (NEW) — only relevant for road vehicles */}
          {form.type !== "boat" && (
          <div className="rounded-2xl border border-[#0a0a0a]/10 p-5 bg-[#fafaf7]" data-testid="sell-periodic-section">
            <div className="overline overline-gold mb-3 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {lang === "ar" ? "الفحص الفني الدوري (سلامة/فحص)" : "Periodic technical inspection (Salama/Fahas)"}</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.periodic_inspection_valid}
                onChange={(e) => set("periodic_inspection_valid", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#c9a55c]"
                data-testid="sell-periodic-valid"
              />
              <span className="text-sm text-[#3a3a3a] leading-snug">
                {lang === "ar"
                  ? "المركبة مفحوصة فحصاً فنياً دورياً سارياً (إلزامي لنقل الملكية)."
                  : "Vehicle has a valid periodic technical inspection (required for ownership transfer)."}
              </span>
            </label>
            {form.periodic_inspection_valid && (
              <div className="mt-3">
                <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "تاريخ انتهاء الفحص الدوري" : "Inspection expiry date"}</label>
                <input
                  type="date"
                  className="input-dark mt-1 max-w-xs"
                  value={form.periodic_inspection_expiry}
                  onChange={(e) => set("periodic_inspection_expiry", e.target.value)}
                  data-testid="sell-periodic-expiry"
                />
              </div>
            )}
            {!form.periodic_inspection_valid && (
              <p className="text-[11px] text-[#8a6f2e] mt-3 leading-relaxed">
                {lang === "ar"
                  ? "لا تقلق — يمكن لمشتري السيارة حجز فحص دوري في سلامة أو فحص مباشرة من صفحة الإعلان."
                  : "No worries — buyers can book a periodic inspection at Salama or Fahas directly from the listing."}
              </p>
            )}
          </div>
          )}

          {/* ── CARTAL Escrow opt-in — hidden while regulator-gated ──────── */}
          {FEATURES.ESCROW_ENABLED && (
          <div className="rounded-2xl border-2 border-[#c9a55c]/40 p-5 bg-gradient-to-br from-[#fdf9ef] to-white" data-testid="sell-escrow-section">
            <div className="overline overline-gold mb-3 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> {lang === "ar" ? "ضمان كارتال (اختياري)" : "CARTAL Escrow (optional)"}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.escrow_enabled}
                onChange={(e) => set("escrow_enabled", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#c9a55c]"
                data-testid="sell-escrow-toggle"
              />
              <span className="text-sm text-[#3a3a3a] leading-snug">
                {lang === "ar"
                  ? (form.type === "boat"
                      ? "أقبل استخدام ضمان كارتال على هذا الإعلان: تُحجز أموال المشتري حتى يستلم اليخت / الزورق ويوافق. سيظهر إعلاني بشارة \"محمي بضمان كارتال\"."
                      : "أقبل استخدام ضمان كارتال على هذا الإعلان: تُحجز أموال المشتري حتى يستلم السيارة ويوافق. سيظهر إعلاني بشارة \"محمي بضمان كارتال\".")
                  : (form.type === "boat"
                      ? "I accept using CARTAL Escrow for this listing: buyer funds are held until the yacht/boat is delivered and accepted. My listing will display a \"Protected by CARTAL Escrow\" badge."
                      : "I accept using CARTAL Escrow for this listing: buyer funds are held until the car is delivered and accepted. My listing will display a \"Protected by CARTAL Escrow\" badge.")}
              </span>
            </label>
            {form.escrow_enabled && (
              <div
                className="mt-4 rounded-xl p-3 text-[11px] leading-relaxed"
                style={{
                  backgroundColor: "rgba(201,165,92,0.10)",
                  border: "1px solid rgba(201,165,92,0.45)",
                  color: "#5a4615",
                }}
              >
                <strong>{lang === "ar" ? "تنويه قانوني:" : "Legal notice:"}</strong>
                <span className="ms-1">
                  {lang === "ar"
                    ? (form.type === "boat"
                        ? "كارتال مزوّد تقني للضمان فقط، ولا تتحمل أي مسؤولية قانونية أو مالية عن اليخت / الزورق، حالته، ملكيته، أو التزامات أي من الطرفين. الضمان آلية احتجاز مالي بين البائع والمشتري وفق "
                        : "كارتال مزوّد تقني للضمان فقط، ولا تتحمل أي مسؤولية قانونية أو مالية عن المركبة، حالتها، ملكيتها، أو التزامات أي من الطرفين. الضمان آلية احتجاز مالي بين البائع والمشتري وفق ")
                    : (form.type === "boat"
                        ? "CARTAL is a purely technical escrow facilitator and bears no legal or financial liability for the yacht/boat, its condition, ownership, or either party's obligations. Escrow is a financial holding mechanism between the seller and buyer per "
                        : "CARTAL is a purely technical escrow facilitator and bears no legal or financial liability for the vehicle, its condition, ownership, or either party's obligations. Escrow is a financial holding mechanism between the seller and buyer per ")}
                </span>
                <a
                  href="/escrow-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold"
                  data-testid="link-escrow-terms"
                >
                  {lang === "ar" ? "شروط ضمان كارتال" : "CARTAL Escrow Terms"}
                </a>.
              </div>
            )}
          </div>
          )}

          {err && <div className="text-sm text-red-600" data-testid="sell-error">{err}</div>}
          <button className="btn-primary w-full justify-center" disabled={busy} data-testid="sell-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {lang === "ar" ? "انشر الإعلان" : "Publish listing"}
          </button>
        </form>

        <div className="surface rounded-3xl p-8 lg:sticky lg:top-24 lg:self-start">
          {/* iter323 — Section-aware Fair Price panel.
              The existing AI valuation engine (`POST /api/ai/valuation`
              → `estimate_vehicle_value`) is CAR-specific: it prompts the
              LLM with make / model / year / mileage against the Saudi
              car market. Sending a boat, jet-ski, heavy machine,
              motorcycle, scrap unit or export asset through it produces
              nonsense such as "Unable to identify vehicle model" —
              which the user saw on a marine listing.
              Architecture: route by `form.type`. Cars keep the exact
              same panel + engine (no regression). Every other section
              gets a neutral, section-appropriate placeholder that
              explicitly states the AI valuation isn't available yet for
              its asset class. When a Marine / Heavy / Motorcycle / Scrap /
              Export engine is added later, add a branch here.
              `CAR_FAIR_PRICE_TYPES` is the single source of truth for
              which types are considered "cars" by the pricing pipeline. */}
          {["used", "new", "rental", "export", "salvage"].includes(form.type) ? (
            <>
              <div className="overline mb-2">{lang === "ar" ? "اقتراح السعر بالذكاء الاصطناعي" : "AI PRICE SUGGESTION"}</div>
              <h2 className="font-display text-2xl">{lang === "ar" ? "ما هو السعر العادل؟" : "What's a fair price?"}</h2>
              <p className="text-sm text-[#525252] mt-2">{lang === "ar" ? "املأ Make / Model / Year / Mileage ثم اضغط أدناه." : "Fill make/model/year/mileage above then tap below."}</p>
              <button type="button" onClick={estimate} disabled={valBusy || !form.make || !form.model} className="btn-primary mt-5 w-full justify-center text-sm" data-testid="sell-ai-estimate">
                {valBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {valBusy ? t("common.estimating") : t("common.estimate")}
              </button>
              {valuation && (
                <div className="mt-6 space-y-3" data-testid="sell-ai-result">
                  <div>
                    <div className="text-xs text-[#6b6b6b]">{t("common.fairValue")}</div>
                    <div className="font-display text-3xl text-[#8a6f2e]">{formatSAR(valuation.fair_sar)} <span className="text-xs text-[#6b6b6b]">SAR</span></div>
                  </div>
                  <div className="text-xs text-[#6b6b6b]">{t("common.range")}: {formatSAR(valuation.low_sar)} – {formatSAR(valuation.high_sar)}</div>
                  <p className="text-sm text-[#525252] leading-relaxed">{valuation.summary}</p>
                  <button type="button" className="btn-ghost text-xs" onClick={() => set("price_sar", valuation.fair_sar)}>
                    {lang === "ar" ? "استخدم هذا السعر" : "Use this price"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div data-testid="sell-ai-unavailable">
              <div className="overline mb-2">{lang === "ar" ? "اقتراح السعر بالذكاء الاصطناعي" : "AI PRICE SUGGESTION"}</div>
              <h2 className="font-display text-2xl">
                {form.type === "boat"
                  ? (lang === "ar" ? "تقييم بحري" : "Marine valuation")
                  : form.type === "heavy"
                  ? (lang === "ar" ? "تقييم المعدات الثقيلة" : "Heavy-equipment valuation")
                  : form.type === "motorcycle"
                  ? (lang === "ar" ? "تقييم الدراجات النارية" : "Motorcycle valuation")
                  : form.type === "salvage"
                  ? (lang === "ar" ? "تقييم السكراب" : "Scrap valuation")
                  : (lang === "ar" ? "تقييم هذا القسم" : "Valuation for this section")}
              </h2>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">
                {lang === "ar"
                  ? "منظومة السعر العادل الحالية مصمَّمة للسيارات فقط. سيتم إتاحة مسار تقييم مستقل لهذا القسم قريباً. حدّد سعرك يدوياً في الأعلى، ويمكنك تمييز إعلانك للوصول إلى جمهور أوسع."
                  : "The current AI Fair Price engine is calibrated for cars only. A dedicated valuation path for this section will be enabled soon. Set your price manually above, and consider promoting your listing for wider reach."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * CitySearchBox — iter204 UI-only addition.
 * Renders directly under the existing City dropdown. As soon as the user
 * types at least 2 characters, matching cities from the SAME `CITIES`
 * list appear as suggestions (Arabic-tolerant: unifies alef/yaa/ha
 * variants + strips tatweel). Clicking a suggestion sets the parent
 * form.city so the dropdown updates instantly. No new data source, no
 * new API — pure client-side filter over the current list.
 * ───────────────────────────────────────────────────────────────────── */
function _arNorm(s) {
  return (s || "")
    .replace(/ـ/g, "")
    .replace(/[أإآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .toLowerCase()
    .trim();
}

function CitySearchBox({ lang, value, onSelect }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const suggestions = React.useMemo(() => {
    const raw = q.trim();
    if (raw.length < 2) return [];
    const nq = _arNorm(raw);
    return CITIES
      .filter(([, en, ar]) => _arNorm(ar).includes(nq) || _arNorm(en).includes(nq))
      .slice(0, 20);
  }, [q]);
  return (
    <div className="relative mt-2">
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={lang === "ar" ? "ابحث عن مدينتك… (حرفان على الأقل)" : "Search your city… (min 2 chars)"}
        className="input-dark"
        data-testid="sell-city-search"
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-20 top-full mt-1 start-0 end-0 max-h-60 overflow-auto rounded-xl border border-[#0a0a0a]/10 bg-white shadow-lg"
          data-testid="sell-city-suggestions"
        >
          {suggestions.map(([id, en, ar]) => (
            <li key={id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelect(id); setQ(lang === "ar" ? ar : en); setOpen(false); }}
                className={`w-full text-start px-3 py-2 text-sm hover:bg-[#fdf9ef] ${value === id ? "bg-[#fdf9ef] font-semibold text-[#8a6f2e]" : "text-[#0a0a0a]"}`}
                data-testid={`sell-city-suggestion-${id}`}
              >
                {lang === "ar" ? ar : en}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q.trim().length >= 2 && suggestions.length === 0 && (
        <div className="absolute z-20 top-full mt-1 start-0 end-0 rounded-xl border border-[#0a0a0a]/10 bg-white shadow-lg px-3 py-2 text-xs text-[#8a8a8a]">
          {lang === "ar" ? "لا توجد مدينة مطابقة." : "No matching city."}
        </div>
      )}
    </div>
  );
}

